import { Apple, ChevronDown, Copy, Moon, Plus, Repeat, Sparkles, Sun, Sunrise } from 'lucide-react';
import { useId, useMemo } from 'react';
import type { Entry, MealView, TargetView, UsualMeal } from '../../shared/api';
import { loggedWithAI } from '../../shared/sources';
import { MEAL_LABELS, type Meal } from '../../shared/dates';
import { scaleMap, type NutrientDef } from '../../shared/nutrients';
import { colorFor, kcal } from '../lib/format';
import { macroSummary, mealNutrition } from '../lib/nutrition';
import { NutritionList } from './NutritionPanel';

const ICONS: Record<Meal, typeof Sun> = { breakfast: Sunrise, lunch: Sun, dinner: Moon, snack: Apple };

interface Props {
  view: MealView;
  yesterdayCount: number;
  usual?: UsualMeal;
  /** Whether the meal's full nutrition shows. An empty meal has none to show. */
  open: boolean;
  defs: Map<string, NutrientDef>;
  tracked: string[];
  targets: TargetView[];
  onToggle: () => void;
  onAdd: () => void;
  onOpenEntry: (entry: Entry) => void;
  onCopyYesterday: () => void;
  onLogUsual: (usual: UsualMeal) => void;
}

export function MealSection({ view, yesterdayCount, usual, open, defs, tracked, targets, onToggle, onAdd, onOpenEntry, onCopyYesterday, onLogUsual }: Props) {
  const Icon = ICONS[view.meal];
  const label = MEAL_LABELS[view.meal];
  const panelId = useId();
  const empty = view.entries.length === 0;
  const expanded = open && !empty;
  // Worked out from the foods (the same sums as view.totals) so partial sums can show "≥".
  const nutrition = useMemo(() => mealNutrition(view.entries), [view.entries]);
  const macros = macroSummary(nutrition.totals, nutrition);
  // A daily food's copies on their own still leave the meal to fill: keep the shortcuts.
  const onlyDaily = view.entries.every((e) => e.recurringId);
  return (
    <section className="meal" aria-label={label}>
      <div className="meal__head">
        <span className={`meal__icon meal__icon--${view.meal}`} aria-hidden="true">
          <Icon size={18} />
        </span>
        <div className="grow">
          <div className="meal__title">{label}</div>
          {/* The glance is also the toggle for the full breakdown. Each macro stays whole when the line wraps.
              The name starts with what it opens and still holds every visible word ("label in name"). */}
          {!empty && (
            <button
              type="button"
              className="meal__sum num"
              aria-label={`${label} nutrition: ${macros.map((m) => m.spoken).join(', ')}`}
              aria-expanded={expanded}
              aria-controls={expanded ? panelId : undefined}
              onClick={onToggle}
            >
              {macros.map((m, i) => (
                <span key={m.key} className="meal__macro">
                  {m.key !== 'kcal' && <span className="macro__swatch" style={{ background: colorFor(m.key) }} aria-hidden="true" />}
                  {m.text}
                  {i === macros.length - 1 && <ChevronDown size={15} className="meal__chev" aria-hidden="true" />}
                </span>
              ))}
            </button>
          )}
        </div>
        <button className="btn btn--ghost btn--icon btn--sm" aria-label={`Add to ${label}`} onClick={onAdd}>
          <Plus size={18} />
        </button>
      </div>

      {expanded && (
        <div id={panelId} className="meal__nutrition">
          <NutritionList amounts={nutrition.totals} defs={defs} tracked={tracked} coverage={nutrition} itemNoun="foods" targets={targets} />
        </div>
      )}

      {view.entries.map((e) => {
        const n = scaleMap(e.per100, e.grams);
        return (
          <button key={e.id} className="entry" onClick={() => onOpenEntry(e)}>
            <div className="grow">
              <div className="entry__name">{e.name}</div>
              <div className="entry__meta num">
                {Math.round(e.grams)} g{e.brand ? ` · ${e.brand}` : ''}
                {loggedWithAI(e) && (
                  <span className="badge badge--ai" style={{ marginLeft: 6 }}>
                    <Sparkles size={10} /> AI
                  </span>
                )}
                {/* A daily food's copy, so it's clear why it's in the log (also after that daily food stopped). */}
                {(e.recurringId || e.source === 'recurring') && (
                  <span className="badge" style={{ marginLeft: 6 }} title={e.recurringId ? 'Added every day' : 'Added by a daily food you stopped'}>
                    <Repeat size={10} aria-hidden="true" /> Daily
                  </span>
                )}
              </div>
            </div>
            <span className="entry__kcal num">{kcal(n.kcal)}</span>
          </button>
        );
      })}

      {onlyDaily && (usual || yesterdayCount > 0) && (
        <div className="meal__empty">
          {usual && (
            <button className="chip chip--accent" onClick={() => onLogUsual(usual)} title={usual.items.map((i) => i.name).join(', ')}>
              <Plus size={14} /> {usual.title} · {kcal(usual.totals.kcal)} kcal
            </button>
          )}
          {yesterdayCount > 0 && (
            <button className="chip" onClick={onCopyYesterday}>
              <Copy size={14} /> Copy yesterday's {label.toLowerCase()}
            </button>
          )}
        </div>
      )}
    </section>
  );
}
